import type { PitchTrackerEvent, PitchTrackerEventName } from '@muzly/shared';

/**
 * eventLog.ts — 이벤트 로그/인스펙터 패널(#10)의 순수 수집 로직.
 *
 * `pitchTrackerEvent` 스트림을 롤링 버퍼(상한 N)로 접는다. 렌더와 무관한 순수
 * 함수만 두어 vitest로 검증한다(샘플링/캡/분류). 불변식: raw PCM은 다루지 않고
 * `PitchTrackerEvent`(Analysis Frame 메타)만 보관한다.
 */

// ── 분류 ───────────────────────────────────────────────────────────────────

/** 타입 필터 카테고리 — GOAL.md "타입 필터" 7종. */
export type EventCategory =
  | 'pitch_frame'
  | 'session'
  | 'input_level'
  | 'discontinuity'
  | 'performance'
  | 'interruption'
  | 'error';

/** 화면 표시 순서/필터 칩 순서. */
export const EVENT_CATEGORIES: EventCategory[] = [
  'pitch_frame',
  'session',
  'input_level',
  'performance',
  'discontinuity',
  'interruption',
  'error',
];

/** 색 하이라이트 대상(이산 사건). */
export const DISCRETE_CATEGORIES: EventCategory[] = [
  'error',
  'interruption',
  'discontinuity',
];

const CATEGORY_OF: Record<PitchTrackerEventName, EventCategory> = {
  pitch_frame: 'pitch_frame',
  session_started: 'session',
  session_stopped: 'session',
  config_changed: 'session',
  input_level: 'input_level',
  stream_discontinuity: 'discontinuity',
  performance_metrics: 'performance',
  interruption_started: 'interruption',
  interruption_ended: 'interruption',
  error: 'error',
};

export const categorize = (type: PitchTrackerEventName): EventCategory =>
  CATEGORY_OF[type];

export const isDiscrete = (category: EventCategory): boolean =>
  DISCRETE_CATEGORIES.includes(category);

// ── 상태 ───────────────────────────────────────────────────────────────────

export interface LoggedEvent {
  /** 단조 증가 시퀀스(렌더 key + 선택 식별). */
  seq: number;
  category: EventCategory;
  type: PitchTrackerEventName;
  /** 수집 시각(ms). */
  atMs: number;
  event: PitchTrackerEvent;
}

export interface EventLogState {
  /** 오래된→최신 순. 상한 초과 시 앞에서 버린다. */
  events: LoggedEvent[];
  seq: number;
  /** pitch_frame 누적 수신 수(샘플링 판정용 — 버린 것 포함). */
  pitchFrameSeen: number;
}

export const initialEventLogState: EventLogState = {
  events: [],
  seq: 0,
  pitchFrameSeen: 0,
};

/** 롤링 버퍼 상한(상한 N). */
export const MAX_EVENTS = 500;
/** pitch_frame 폭주 대비 샘플링 — N개당 1개 보관(1이면 전부). */
export const PITCH_FRAME_SAMPLING = 5;

export interface AppendOptions {
  cap: number;
  pitchFrameSampling: number;
}

export const DEFAULT_APPEND_OPTIONS: AppendOptions = {
  cap: MAX_EVENTS,
  pitchFrameSampling: PITCH_FRAME_SAMPLING,
};

const pushCapped = (
  events: LoggedEvent[],
  next: LoggedEvent,
  cap: number,
): LoggedEvent[] => {
  const appended = [...events, next];
  return appended.length > cap
    ? appended.slice(appended.length - cap)
    : appended;
};

/**
 * 이벤트 1건을 버퍼에 접는다(순수). pitch_frame은 샘플링으로 솎고, 나머지(이산
 * 사건 포함)는 항상 보관한다. 버려진 pitch_frame도 `pitchFrameSeen`엔 센다.
 */
export const appendEvent = (
  state: EventLogState,
  event: PitchTrackerEvent,
  atMs: number,
  options: AppendOptions = DEFAULT_APPEND_OPTIONS,
): EventLogState => {
  const category = categorize(event.type);

  if (category === 'pitch_frame') {
    const pitchFrameSeen = state.pitchFrameSeen + 1;
    const sampling = Math.max(1, options.pitchFrameSampling);
    const keep = pitchFrameSeen % sampling === 1 || sampling === 1;
    if (!keep) {
      return { ...state, pitchFrameSeen };
    }
    const logged: LoggedEvent = {
      seq: state.seq,
      category,
      type: event.type,
      atMs,
      event,
    };
    return {
      events: pushCapped(state.events, logged, options.cap),
      seq: state.seq + 1,
      pitchFrameSeen,
    };
  }

  const logged: LoggedEvent = {
    seq: state.seq,
    category,
    type: event.type,
    atMs,
    event,
  };
  return {
    ...state,
    events: pushCapped(state.events, logged, options.cap),
    seq: state.seq + 1,
  };
};
